import * as stateManagement from '#packages/stateManagement';
import { EditorType, PanelResolveType } from '@wix/platform-editor-sdk';
import { ErrorReporter } from '@wix/editor-error-reporter';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import { deleteReplacerAppAndResetModal } from './pageReplaceService';
import biEvents from '../bi/events';
import type { AppData } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from '@wix/document-services-types/src';
import { DeleteAppOriginSource } from '@wix/editor-platform-host-integration/ui';
import { isResponsiveEditor } from '@wix/santa-editor-utils';
import type { AppDefinitionId } from '@wix/document-services-types';

interface UninstallOrigin {
  source: number;
  name: string;
  biOrigin: string;
}

type BiReportType = 'start' | 'end' | 'error';

const durations: Record<AppDefinitionId, number> = {};

const reportUninstallBi = (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  origin: UninstallOrigin,
  type: BiReportType,
) => {
  if (type === 'start') {
    durations[appDefinitionId] = Date.now();
  }
  editorAPI.bi.event(biEvents.UninstallPlatformBiEvent, {
    type,
    origin: origin.biOrigin,
    editorType: isResponsiveEditor()
      ? EditorType.Responsive
      : EditorType.Classic,
    appDefinitionId,
    ...(type !== 'start'
      ? { duration: Date.now() - durations[appDefinitionId] }
      : {}),
  });
  if (type !== 'start') {
    delete durations[appDefinitionId];
  }
};

const getUninstallOrigin = (
  editorAPI: EditorAPI,
  options: UninstallAppOptions,
) => {
  const panelOrigin: UninstallOrigin = {
    source: DeleteAppOriginSource.UninstallAction,
    name: '',
    biOrigin: options?.origin ?? 'app_custom_action_sdk',
  };
  if (options?.compRef) {
    const compData = editorAPI.components.data.get(options.compRef);
    if (compData.type === 'Page') {
      panelOrigin.source = DeleteAppOriginSource.EssentialPageDeleted;
      panelOrigin.name = compData.title;
      panelOrigin.biOrigin =
        DeleteAppOriginSource[DeleteAppOriginSource.EssentialPageDeleted];
    } else {
      panelOrigin.source = DeleteAppOriginSource.EssentialWidgetDeleted;
      panelOrigin.biOrigin =
        DeleteAppOriginSource[DeleteAppOriginSource.EssentialWidgetDeleted];
      panelOrigin.name = editorAPI.components.getDisplayName(options.compRef);
    }
  }
  return panelOrigin;
};

function openUninstallProgressBar(
  editorAPI: EditorAPI,
  appDefinitionId: AppDefinitionId,
): void {
  const appData = editorAPI.platform.getAppDataByAppDefId(appDefinitionId);
  const panelProps = {
    title: translate('PLATFORM_Delete_Modal_Apps_Progress_Bar_Header', {
      App_Name: appData.appDefinitionName,
    }),
    progressMax: 1,
    taskDisplayName: translate('PLATFORM_Delete_Modal_Apps_Progress_Bar_Text1'),
    step: 0,
    isDone: false,
  };
  editorAPI.panelManager.openPanel(
    'panels.focusPanels.progressBarPanel',
    panelProps,
    false,
  );
}

function closeUninstallProgressBar(
  editorAPI: EditorAPI,
  success: boolean,
): void {
  const panelProps = success
    ? {
        step: 1,
        isDone: true,
        taskDisplayName: translate(
          'PLATFORM_Delete_Modal_Apps_Progress_Bar_Text3',
        ),
      }
    : {
        isDone: true,
      };
  editorAPI.panelManager.updatePanelProps(
    'panels.focusPanels.progressBarPanel',
    panelProps,
  );
}

const uninstallAppAsync = (
  editorAPI: EditorAPI,
  appDefinitionId: AppDefinitionId,
  origin: UninstallOrigin,
): Promise<void> =>
  new Promise((resolve, reject) => {
    reportUninstallBi(editorAPI, appDefinitionId, origin, 'start');
    editorAPI.dsActions.platform.uninstall(
      [appDefinitionId],
      (...args) => {
        reportUninstallBi(editorAPI, appDefinitionId, origin, 'end');
        resolve(...args);
      },
      (...args) => {
        reportUninstallBi(editorAPI, appDefinitionId, origin, 'error');
        reject(...args);
      },
    );
  });

const beforePagesRemoveHook = (
  editorAPI: EditorAPI,
  pages: string[],
): Promise<void> =>
  new Promise((resolve, reject) => {
    try {
      if (pages.includes(editorAPI.pages.getCurrentPage()?.id)) {
        editorAPI.pages.beforePagesRemoveHook(pages, resolve, {});
      } else {
        resolve();
      }
    } catch (e) {
      reject(e);
    }
  });

const openUninstallConfirmationPanel = (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  options?: {
    leavePanelsOpen?: boolean;
    compRef?: CompRef;
    platformOrigin: UninstallOrigin;
  },
): Promise<ConfirmationPanelObject> =>
  new Promise((resolve) => {
    if (
      stateManagement.platform.selectors.isAppWithReplacerPage(
        editorAPI,
        appDefinitionId,
      )
    ) {
      return deleteReplacerAppAndResetModal(editorAPI, appDefinitionId).then(
        resolve,
      );
    }
    editorAPI.panelManager.openPanel(
      'platformPanels.PlatformDeleteAppModal',
      {
        appDefinitionId,
        onClose: resolve,
        origin: options.platformOrigin,
        compId: options?.compRef?.id,
      },
      { leavePanelsOpen: options?.leavePanelsOpen ?? true },
    );
  });

const getAppsPages = (
  editorAPI: EditorAPI,
  appDefinitionId: AppDefinitionId,
) => {
  const pagesData = editorAPI.dsRead.pages.getPagesData();
  const data = editorAPI.platform.getAppDataByAppDefId(appDefinitionId);
  return pagesData
    .filter(
      (page) =>
        page.managingAppDefId === data.appDefinitionId ||
        page.appDefinitionId === data.appDefinitionId,
    )
    .map((pageData) => pageData.id);
};

export interface UninstallAppOptions {
  origin?: string;
  onSuccess?: () => void;
  onFailure?: (error: Error) => void;
  leavePanelsOpen?: boolean;
  compRef?: CompRef;
}

interface ConfirmationPanelObject {
  status: string;
}

const uninstallApp = async (
  editorAPI: EditorAPI,
  appData: AppData,
  shouldShowModal = true,
  {
    origin,
    onSuccess,
    onFailure,
    leavePanelsOpen,
    compRef,
  }: UninstallAppOptions = {},
) => {
  const platformOrigin = getUninstallOrigin(editorAPI, {
    origin,
    onSuccess,
    onFailure,
    leavePanelsOpen,
    compRef,
  });
  const appDefinitionId = appData?.appDefinitionId;
  if (!appDefinitionId) {
    throw new Error('appData is undefined, must contain appDefinitionId');
  }
  const confirmationStatus: ConfirmationPanelObject = shouldShowModal
    ? await openUninstallConfirmationPanel(editorAPI, appDefinitionId, {
        leavePanelsOpen,
        compRef,
        platformOrigin,
      })
    : { status: PanelResolveType.MAIN_ACTION };
  if (confirmationStatus.status === PanelResolveType.MAIN_ACTION) {
    util.fedopsLogger.interactionStarted(
      util.fedopsLogger.INTERACTIONS.UNINSTALL_APP,
    );
    openUninstallProgressBar(editorAPI, appDefinitionId);
    const pages = getAppsPages(editorAPI, appDefinitionId);
    editorAPI.history.add('before remove app');
    editorAPI.selection.deselectComponents();
    try {
      await beforePagesRemoveHook(editorAPI, pages);
      await uninstallAppAsync(editorAPI, appDefinitionId, platformOrigin);

      await stateManagement.platform.actions.saveInBackground(editorAPI, true);
      closeUninstallProgressBar(editorAPI, true);

      if (onSuccess) {
        onSuccess();
      }

      editorAPI.bi.event(util.bi.events.COMPONENT_REMOVED, {
        removal_method: origin,
        app_id: appDefinitionId,
        app_site_id: appData.instanceId,
      });
      util.fedopsLogger.interactionEnded(
        util.fedopsLogger.INTERACTIONS.UNINSTALL_APP,
      );
    } catch (e) {
      closeUninstallProgressBar(editorAPI, false);
      await editorAPI.history.undo();
      ErrorReporter.captureException(e, {
        tags: { appInstallFailed: appDefinitionId },
        extra: { appData },
      });

      if (onFailure) {
        onFailure(e as Error);
      }

      throw e;
    }
  } else {
    ErrorReporter.captureException(
      new Error(`Application remove canceled by user.`),
      {
        tags: { appInstallFailed: appDefinitionId },
        extra: { appData },
      },
    );
    throw new Error('Application remove canceled.');
  }
};

export { uninstallApp };
